import "./style/index.scss";
import { CheckOutlined, LeftOutlined } from "@ant-design/icons";
import { Form, Row, Col, Input, Divider, Space, Button, InputNumber } from "antd";
import { useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { atualizarCondicaoGeralSorteio, cadastrarCondicaoGeralSorteio } from "../../../../services/condicaoGeralSorteioService";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { CondicaoGeralSorteioModel } from "../../../../models/condicaoGeralSorteio/condicaoGeralModel";
import { percentParaNumber, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { setModalCadastrarCondicaoGeralSorteio } from "../../../../redux/slices/condicaoGeralSorteio/condicaoGeralSorteioSlice";
import { toastWarning } from "../../../../util/toast";

const CondicaoGeralSorteioModal = () => {
  //estados globais
  const condicaoGeralSorteioState = useAppSelector((state) => state.condicaoGeralSorteio);
  const gradePremiacaoState = useAppSelector((state) => state.gradePremiacao);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<CondicaoGeralSorteioModel>();
  const [condicaoGeral, setCondicaoGeral] = useState<CondicaoGeralSorteioModel>({} as CondicaoGeralSorteioModel);

  const editar = Object.values(condicaoGeralSorteioState.entity).length !== 0;

  const inputInicial = useRef<any>(null);

  useEffect(() => {
    if (inputInicial.current && !editar) {
      inputInicial.current.focus();
    }
    if (editar) {
      form.setFieldsValue({
        ...condicaoGeralSorteioState.entity,
        quotaSorteio: condicaoGeralSorteioState.entity.quotaSorteio * 100,
        quotaResgate: condicaoGeralSorteioState.entity.quotaResgate * 100,
        quotaCarregamento: condicaoGeralSorteioState.entity.quotaCarregamento * 100,
      });
    }
  }, [condicaoGeralSorteioState.entity]);

  const submit = (values: CondicaoGeralSorteioModel) => {
    values.quotaSorteio = percentParaNumber(form.getFieldValue("quotaSorteio"));
    values.quotaCarregamento = percentParaNumber(form.getFieldValue("quotaCarregamento"));
    values.quotaResgate = percentParaNumber(form.getFieldValue("quotaResgate"));
    values = { ...values, ...gradePremiacaoState.formGrade };
    const valid = validationsSubmitForm(values);
    if (valid) {
      if (editar) {
        values.id = condicaoGeralSorteioState.entity.id;
        dispatch(atualizarCondicaoGeralSorteio({ value: values, busca: condicaoGeralSorteioState.busca }));
      } else {
        dispatch(cadastrarCondicaoGeralSorteio({ value: values, busca: condicaoGeralSorteioState.busca }));
      }
    }
  };

  const validationsSubmitForm = (value: CondicaoGeralSorteioModel): boolean => {
    const somaQuotas = value.quotaSorteio + value.quotaCarregamento + value.quotaResgate;
    if (somaQuotas < 0.99 || somaQuotas > 1) {
      toastWarning("O somatório das quotas informado deve totalizar 100%");
      return false;
    }
    if (editar) {
      var buscaNumero = condicaoGeralSorteioState.list.find((a: any) => a.numeroCG === value.numeroCG);
      if (buscaNumero !== undefined && buscaNumero.numeroCG !== condicaoGeralSorteioState.entity.numeroCG) {
        toastWarning("Número da condição geral já existe!");
        form.setFieldValue("numeroCG", "");
        return false;
      }
    }
    return true;
  };

  const voltar = () => {
    dispatch(setModalCadastrarCondicaoGeralSorteio(false));
  };

  return (
    <Form layout="vertical" form={form} onFinish={submit}>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item
            label="Número CG"
            name="numeroCG"
            rules={[
              {
                required: true,
                message: "Digite o número da condição geral",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} controls={false} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Número do Processo"
            name="numeroProcesso"
            rules={[
              {
                required: true,
                message: "Digite o número do processo",
              },
            ]}
          >
            <Input style={{ width: "100%" }} type="text" ref={inputInicial} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Tamanho série"
            name="tamanhoSerie"
            rules={[
              {
                required: true,
                message: "Digite o tamanho da série",
              },
            ]}
          >
            <NumericFormat
              className="ant-input noBoderColor number-input"
              style={{ width: "100%" }}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale={true}
              onChange={() => form.setFieldValue("tamanhoSerie", realParaNumber(form.getFieldValue("tamanhoSerie")))}
              onValueChange={(values, sourceInfo) => {
                setCondicaoGeral({
                  ...condicaoGeral,
                  tamanhoSerie: values.floatValue!,
                });
              }}
              valueIsNumericString={false}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Quantidade mínima"
            name="quantidadeVendaMinima"
            rules={[
              {
                required: true,
                message: "Digite a quantidade de venda mínima",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} controls={false} formatter={(value) => (value ? value.toString().replace(".", "") : "")} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            label="Quota sorteio"
            name="quotaSorteio"
            rules={[
              {
                required: true,
                message: "Digite a quota sorteio",
              },
            ]}
          >
            <NumericFormat
              className="ant-input noBoderColor number-input"
              style={{ width: "100%" }}
              decimalSeparator=","
              decimalScale={5}
              suffix="%"
              fixedDecimalScale={true}
              onValueChange={(values, sourceInfo) => {
                setCondicaoGeral({
                  ...condicaoGeral,
                  quotaSorteio: values.floatValue!,
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Quota resgate (Capitalização)"
            name="quotaResgate"
            rules={[
              {
                required: true,
                message: "Digite a quota resgate",
              },
            ]}
          >
            <NumericFormat
              className="ant-input noBoderColor number-input"
              style={{ width: "100%" }}
              decimalSeparator=","
              decimalScale={5}
              suffix="%"
              fixedDecimalScale={true}
              onValueChange={(values, sourceInfo) => {
                setCondicaoGeral({
                  ...condicaoGeral,
                  quotaResgate: values.floatValue!,
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Quota carregamento"
            name="quotaCarregamento"
            rules={[
              {
                required: true,
                message: "Digite a quota carregamento",
              },
            ]}
          >
            <NumericFormat className="ant-input noBoderColor number-input" style={{ width: "100%" }} decimalSeparator="," decimalScale={5} suffix="%" fixedDecimalScale={true} />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <div className="modalFooter">
        <Space size="middle">
          <Button className="cancelButton" onClick={voltar} icon={<LeftOutlined />}>
            VOLTAR
          </Button>
          <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
            {editar ? "ATUALIZAR" : "SALVAR"}
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default CondicaoGeralSorteioModal;
